import type { Collection } from '@/types/collection';

import CollectionCard, { type CollectionCardEventCount } from './CollectionCard/CollectionCard';
import { LoadingSkeleton } from './LoadingSkeleton/LoadingSkeleton';
import { useCollections } from './useCollections';
import { useOrganizerCollectionEventCounts } from './useOrganizerCollectionEventCounts';
import { ShowMoreButton } from '../_shared/ShowMoreButton/ShowMoreButton';
import styles from './Collections.module.scss';

interface Props {
  organizerId: string;
  initialCollections: Collection[];
  initialHasMore: boolean;
  initialTotal: number;
}

function eventCountForCollection(
  colId: string,
  countsLoading: boolean,
  countsError: boolean,
  countById: Record<string, number> | undefined,
): CollectionCardEventCount {
  if (countsLoading) {
    return { status: 'loading' };
  }
  if (countsError) {
    return { status: 'error' };
  }
  return { status: 'success', count: countById?.[colId] ?? 0 };
}

export default function Collections(props: Props) {
  const { collections, isLoading, isError, fetchNextPage, hasMorePages, isFetchingNextPage } =
    useCollections(
      props.organizerId,
      props.initialCollections,
      props.initialHasMore,
      props.initialTotal,
    );

  const {
    data: eventCountById,
    isLoading: countsLoading,
    isError: countsError,
  } = useOrganizerCollectionEventCounts(props.organizerId, collections.length > 0);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError || !collections.length) {
    return null;
  }

  return (
    <div data-testid="organizer-collections">
      <div className={styles.collectionsGrid}>
        {collections.map((col) => (
          <CollectionCard
            key={col.id}
            collection={col}
            eventCount={eventCountForCollection(col.id, countsLoading, countsError, eventCountById)}
          />
        ))}
      </div>
      {hasMorePages && !isLoading && (
        <ShowMoreButton onShowMore={() => fetchNextPage()} isLoadingMore={isFetchingNextPage} />
      )}
    </div>
  );
}
